import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users2, ArrowRight, Loader2, Inbox, Check } from "lucide-react";

// Supervisor caseload — one row per direct report with their active
// Care Seeker count, plus reassign tooling so supervisors can balance
// load across the team. Admin sees the entire org. Other staff see a
// 403 from the API and we bounce to /care-seekers instead.
export default function TeamCaseload() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null); // member whose CSes are visible for reassign
  const [memberCseekers, setMemberCseekers] = useState({}); // memberId → [care seekers]
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetId, setTargetId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/caseload");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const expand = async (memberId) => {
    setExpandedId(memberId === expandedId ? null : memberId);
    setSelectedIds(new Set());
    setTargetId("");
    if (memberId !== expandedId && !memberCseekers[memberId]) {
      try {
        const { data } = await api.get("/care-seekers", { params: { assigned_to: memberId, status: "active" } });
        setMemberCseekers((prev) => ({ ...prev, [memberId]: data || [] }));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const toggleSelect = (csId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(csId)) next.delete(csId);
      else next.add(csId);
      return next;
    });
  };

  const reassign = async () => {
    if (selectedIds.size === 0 || !targetId) return;
    setReassigning(true);
    try {
      const { data: res } = await api.post("/team/caseload/reassign", {
        care_seeker_ids: Array.from(selectedIds),
        new_assignee_id: targetId,
      });
      toast.success(`Reassigned ${res.updated} Care Seeker${res.updated === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      setMemberCseekers({});
      setExpandedId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReassigning(false);
    }
  };

  const reassignTargetOptions = useMemo(() => {
    if (!data) return [];
    return data.members.filter((m) => m.id !== expandedId);
  }, [data, expandedId]);

  if (loading) {
    return <div className="text-center py-12 text-[#1E3A8A]"><Loader2 className="w-6 h-6 animate-spin inline" /> Loading caseload…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="team-caseload-page">
      <div>
        <div className="hw-eyebrow mb-2">Team management</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
          {data.viewer_is_admin ? "Org caseload" : "My team's caseload"}
        </h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Active Care Seekers assigned to each {data.viewer_is_admin ? "team member" : "direct report"}.
          Use Reassign to rebalance the load — the receiving staff member is notified by email and an in-app reminder.
        </p>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl divide-y divide-[#E5E5E0]" data-testid="team-caseload-list">
        {data.members.map((m) => {
          const isOpen = expandedId === m.id;
          const csList = memberCseekers[m.id] || [];
          return (
            <div key={m.id} data-testid={`caseload-row-${m.id}`}>
              <button
                type="button"
                onClick={() => expand(m.id)}
                className="w-full text-left px-5 py-4 hover:bg-[#F9F8F6] flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                    <Users2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0F172A] truncate">{m.name}{m.id === user?.id && <span className="text-xs text-[#1E3A8A] ml-2">(you)</span>}</div>
                    <div className="text-xs text-[#1E3A8A] truncate">{ROLE_LABELS[m.role] || m.role} {m.title ? `· ${m.title}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-display font-semibold text-[#0F172A]" data-testid={`caseload-count-${m.id}`}>
                    {m.active_count}
                  </span>
                  <span className="text-xs text-[#1E3A8A] uppercase tracking-wider">active</span>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 py-4 bg-[#F9F8F6] space-y-3" data-testid={`caseload-detail-${m.id}`}>
                  {csList.length === 0 ? (
                    <div className="text-sm text-[#1E3A8A]">No active Care Seekers assigned.</div>
                  ) : (
                    <>
                      <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
                        {csList.map((cs) => (
                          <li key={cs.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#E5E5E0]">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(cs.id)}
                              onChange={() => toggleSelect(cs.id)}
                              data-testid={`caseload-cs-${cs.id}`}
                              className="flex-shrink-0"
                            />
                            <Link to={`/care-seekers/${cs.id}`} className="text-sm text-[#0F172A] hover:text-[#1E3A8A] flex-1 truncate">
                              <span className="font-medium">{cs.preferred_name || cs.first_name} {cs.last_name}</span>
                              {cs.display_id && <span className="font-mono text-[10px] text-[#1E3A8A] ml-2">{cs.display_id}</span>}
                            </Link>
                            <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{cs.status}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-end gap-2 flex-wrap pt-2 border-t border-[#E5E5E0]">
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-xs font-medium text-[#334155] mb-1">Reassign selected to</label>
                          <select
                            value={targetId}
                            onChange={(e) => setTargetId(e.target.value)}
                            data-testid={`caseload-target-${m.id}`}
                            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
                          >
                            <option value="">— Pick teammate —</option>
                            {reassignTargetOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>{opt.name} · {opt.active_count} active</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={reassign}
                          disabled={reassigning || selectedIds.size === 0 || !targetId}
                          data-testid={`caseload-reassign-${m.id}`}
                          className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-2 disabled:opacity-40 hover:bg-[#172554]"
                        >
                          {reassigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                          Reassign {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.unassigned_count > 0 && (
        <div className="bg-white border border-[#FCD34D] rounded-2xl p-4 flex items-center justify-between" data-testid="caseload-unassigned-banner">
          <div className="flex items-center gap-3">
            <Inbox className="w-5 h-5 text-[#92400E]" />
            <div>
              <div className="font-medium text-[#0F172A]">{data.unassigned_count} active Care Seeker{data.unassigned_count === 1 ? "" : "s"} unassigned</div>
              <div className="text-xs text-[#92400E]">No navigator listed — assign them so someone has eyes on the case.</div>
            </div>
          </div>
          <Link
            to="/care-seekers?assigned_to=unassigned"
            className="px-4 py-2 rounded-full bg-[#92400E] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#78350F]"
            data-testid="caseload-view-unassigned"
          >
            <Check className="w-3.5 h-3.5" /> Assign them
          </Link>
        </div>
      )}
    </div>
  );
}
